import React, { FC, MouseEventHandler, useEffect, useState } from 'react'
import { Typography } from '@mui/material'
import { DateFormatter, currency } from '@components/ui'
import AdressIcon from '@assets/images/svg/ico-adresse.svg'
import AddAddressIcon from '@mui/icons-material/AddLocation'
import { EstimateLine } from '@common/models/estimateLine/estimateLine'
import { ShippingInfo } from '@common/models/shippingInfo/shippingInfo'
import DeliveryMenuCard, { DeliveryMenuCardProps } from '@components/delivery/DeliveryMenuCard'
import moment from 'moment'

export type MenuCardProps = {
  items: Array<EstimateLine>
}

type DayFormatterProps = {
  day: number
  items: Array<EstimateLine>
  Card: FC<MenuCardProps> | FC<DeliveryMenuCardProps>
  onSelectAddress?: Function
}

type ShowAddressProps = {
  shippingInfo: ShippingInfo
}

const ShowAddress: FC<ShowAddressProps> = ({ shippingInfo }) => {
  const { customerAddress, unitPriceIncludingTaxes: { amount = null } = {} } = shippingInfo || {}

  const {
    address: {
      label = 'Retrait boutique',
      line1 = '',
      line2 = '',
      city: { label: cityLabel = '' } = {},
      section: { label: sectionLabel = '' } = {},
      zipCode: { label: zipCodeLabel = '' } = {}
    } = {}
  } = customerAddress || {}

  return (
    <div className="flex w-full rounded-md border-[1px] border-super-ya-blue3 bg-super-ya-blue3 bg-opacity-15 p-5 md:w-1/2">
      <AdressIcon className="mr-2 text-super-ya-blue3" fontSize="small" />
      <div className="flex flex-1 flex-col">
        <div className="flex flex-1 flex-row justify-between">
          <Typography className="text-super-ya-blue3">{label}</Typography>
          {amount && (
            <Typography className="font-bold text-super-ya-blue3">
              {amount} {currency}
            </Typography>
          )}
        </div>
        <Typography>{line1}</Typography>
        <Typography>{line2}</Typography>
        <Typography>
          {zipCodeLabel} {cityLabel} {sectionLabel && ' - ' + sectionLabel}
        </Typography>
      </div>
    </div>
  )
}

type SelectAddressProps = {
  onSelectAddress: MouseEventHandler
}

const SelectAddress: FC<SelectAddressProps> = ({ onSelectAddress }) => {
  return (
    <button type="button" className="border-superYa-gray1 flex w-full flex-col items-center justify-center rounded border-[1px] p-5 md:w-1/2" onClick={onSelectAddress}>
      <AddAddressIcon className="text-super-ya-blue3" />
      <Typography component="p" variant="body1" className="pt-2">
        Choisir une autre adresse
      </Typography>
    </button>
  )
}

interface IMenuGroup {
  [key: string]: Array<EstimateLine>
}

const getCard = (day: number, items: Array<EstimateLine>, Card: FC<MenuCardProps> | FC<DeliveryMenuCardProps>) => {
  // group only custom menu
  let menus: IMenuGroup = {}
  const delivery = Card.name === DeliveryMenuCard.name
  items.forEach((menu: EstimateLine) => {
    const { package: { tidyLabel: labelP } = {}, planning: { packageGroup: { label } = {} } = {}, variant } = menu
    const l: string = delivery ? '' : '_' + labelP
    const labelMenu: string = label + (variant !== undefined ? '_custom' : l)

    // use hasOwnProperty from Object.prototype to be safer - lint advice
    if (!Object.prototype.hasOwnProperty.call(menus, labelMenu)) {
      menus[labelMenu] = [menu]
    } else {
      menus[labelMenu] = [...menus[labelMenu], menu]
    }
  })

  return Object.entries(menus).map(([date, menu]) => {
    return (
      <li key={date}>
        <Card items={menu} />
      </li>
    )
  })
}

const DayFormatter: FC<DayFormatterProps> = ({ day, items, Card, onSelectAddress }) => {
  const [date, setDate] = useState<Date>(null)

  useEffect(() => {
    // check shipping date
    const item = items.find(el => {
      let shippingDate = el.shippingDate
      if (typeof el.shippingDate === 'string') {
        shippingDate = new Date(el.shippingDate)
      }
      return el.planning.date.getTime() !== shippingDate.getTime()
    })

    if (item) {
      setDate(item.shippingDate)
    }
  }, [items])

  return (
    <li className="my-[30px]">
      {items.length > 0 ? (
        <>
          <div className="my-[20px] text-super-ya-blue3">
            <DateFormatter date={new Date(day)} />
          </div>
          <ul className="flex flex-col gap-y-[10px] sm:gap-y-[15px]">{getCard(day, items, Card)}</ul>
          {onSelectAddress && (
            <div className="my-5">
              <Typography>Choisir une adresse :</Typography>
              {date && <Typography className="mt-2 font-bold text-super-ya-red">Attention, la livraison est prévue pour le {moment(date).format('DD MMMM yyyy')}</Typography>}
              <div className="my-5 flex flex-col gap-8 md:flex-row">
                <ShowAddress shippingInfo={items[0].shippingInfo} />
                <SelectAddress onSelectAddress={() => onSelectAddress(day)} />
              </div>
            </div>
          )}
        </>
      ) : null}
    </li>
  )
}

export default DayFormatter
